import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Tooltip,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tabs,
    Tab,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
} from '@mui/material';
import MarkdownRenderer from '../../../components/MarkdownRenderer';
import {
    CloudUpload as CloudUploadIcon,
    LinkedIn as LinkedInIcon,
    Description as DescriptionIcon,
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Folder as FolderIcon,
    Assessment as AssessmentIcon,
    ExpandMore as ExpandMoreIcon,
    History as HistoryIcon,
    CalendarToday as CalendarTodayIcon,
    Analytics as AnalyticsIcon,
    Computer as ComputerIcon,
    School as SchoolIcon,
    Palette as PaletteIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useAppTheme } from '../../../App';
import { DocumentService } from '../services/documentService';
import { UserDocument, ProfileAnalysis } from '../types/document.types';
import { analyzeGTVProfile, getGTVProfileAnalyses, deleteGTVProfileAnalysis } from '../services/gtvService';
import AuthLayout from '../../../components/AuthLayout';
import DocumentManagerDialog from './DocumentManagerDialog';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useSubscription } from '../../../hooks/useSubscription';
import { checkProfileAnalysisLimit, incrementProfileAnalysisCount } from '../../../services/userService';
import { getAuth } from 'firebase/auth';
import { incrementProfileScored } from '../../../services/analyticsService';

const ProfileScoreAnalyzer: React.FC = () => {
    const { getThemeStyles, isDarkMode } = useAppTheme();
    const theme = getThemeStyles();
    const { 
        trackFeatureUsage, 
        trackCustomEvent, 
        trackError 
    } = useAnalytics();
    
    const { isPro, hasFeature } = useSubscription();
    const additionalDocumentsLimit = isPro ? 10 : 3;

    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    
    const [uploadStatus, setUploadStatus] = useState({
        linkedin: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
        cv: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
        additional: 'idle' as 'idle' | 'uploading' | 'success' | 'error',
    });

    const [uploadedDocs, setUploadedDocs] = useState<{
        linkedin?: UserDocument;
        cv?: UserDocument;
        additional: UserDocument[];
    }>({
        additional: [],
    });

    const [alertMessage, setAlertMessage] = useState<{
        type: 'success' | 'error' | 'info';
        message: string;
    } | null>(null);

    const [documentManagerOpen, setDocumentManagerOpen] = useState(false);
    const [analysisResult, setAnalysisResult] = useState<{
        analysis: string;
        user_has_documents: boolean;
        document_count: {
            cv: number;
            linkedin: number;
            additional: number;
        };
        linkedin_provided: boolean;
    } | null>(null);

    const [analysisLimit, setAnalysisLimit] = useState<{
        canAnalyze: boolean;
        remainingCount: number;
        resetDate: string;
    } | null>(null);

    const [pastAnalyses, setPastAnalyses] = useState<ProfileAnalysis[]>([]);
    const [isLoadingPastAnalyses, setIsLoadingPastAnalyses] = useState(false);

    // Tab state
    const [currentTab, setCurrentTab] = useState(0);
    
    // Category selection state
    const [selectedCategory, setSelectedCategory] = useState<'digital_technology' | 'academia_research' | 'arts_culture'>('digital_technology');

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };
    
    const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCategory(event.target.value as 'digital_technology' | 'academia_research' | 'arts_culture');
    };

    // Load existing documents on component mount
    useEffect(() => {
        const loadExistingDocuments = async () => {
            setIsLoadingDocuments(true);
            try {
                const response = await DocumentService.getUserDocuments();
                if (response.status === 'success') {
                    const { documents } = response;
                    
                    // Organize documents by type
                    const linkedinDoc = documents.find(doc => doc.fileType === 'linkedin');
                    const cvDoc = documents.find(doc => doc.fileType === 'cv');
                    const additionalDocs = documents.filter(doc => doc.fileType === 'additional');

                    // Update state with existing documents
                    setUploadedDocs({
                        linkedin: linkedinDoc,
                        cv: cvDoc,
                        additional: additionalDocs,
                    });

                    // Update upload status for existing documents
                    setUploadStatus({
                        linkedin: linkedinDoc ? 'success' : 'idle',
                        cv: cvDoc ? 'success' : 'idle',
                        additional: additionalDocs.length > 0 ? 'success' : 'idle',
                    });

                    // Set LinkedIn URL if it exists
                    if (linkedinDoc?.linkUrl) {
                        setLinkedinUrl(linkedinDoc.linkUrl);
                    }
                }
            } catch (error) {
                console.error('Error loading existing documents:', error);
                setAlertMessage({
                    type: 'error',
                    message: 'Failed to load existing documents'
                });
            } finally {
                setIsLoadingDocuments(false);
            }
        };

        loadExistingDocuments();
    }, []);

    // LinkedIn URL handling
    const handleLinkedInSubmit = async () => {
        if (!linkedinUrl.trim()) {
            setAlertMessage({ type: 'error', message: 'Please enter your LinkedIn URL' });
            return;
        }

        setUploadStatus(prev => ({ ...prev, linkedin: 'uploading' }));
        
        try {
            const response = await DocumentService.uploadDocument({
                fileType: 'linkedin',
                linkUrl: linkedinUrl,
            });
            
            if (response.status === 'success' && response.document) {
                setUploadedDocs(prev => ({ ...prev, linkedin: response.document }));
                setUploadStatus(prev => ({ ...prev, linkedin: 'success' }));
                setAlertMessage({ type: 'success', message: response.message });
            } else {
                setUploadStatus(prev => ({ ...prev, linkedin: 'error' }));
                setAlertMessage({ type: 'error', message: response.message });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, linkedin: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to save LinkedIn profile' });
        }
    };

    // CV file handling
    const onCvDrop = useCallback(async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        const file = acceptedFiles[0];
        setUploadStatus(prev => ({ ...prev, cv: 'uploading' }));

        try {
            const response = await DocumentService.uploadDocument({
                fileType: 'cv',
                file: file,
            });
            
            if (response.status === 'success' && response.document) {
                setUploadedDocs(prev => ({ ...prev, cv: response.document }));
                setUploadStatus(prev => ({ ...prev, cv: 'success' }));
                setAlertMessage({ type: 'success', message: response.message });
            } else {
                setUploadStatus(prev => ({ ...prev, cv: 'error' }));
                setAlertMessage({ type: 'error', message: response.message });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, cv: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to upload CV' });
        }
    }, []);

    // Additional documents handling
    const onAdditionalDrop = useCallback(async (acceptedFiles: File[]) => {
        const remainingSlots = additionalDocumentsLimit - uploadedDocs.additional.length;
        if (acceptedFiles.length > remainingSlots) {
            setAlertMessage({ 
                type: 'error', 
                message: `You can only upload ${remainingSlots} more additional document(s)` 
            });
            return;
        }

        setUploadStatus(prev => ({ ...prev, additional: 'uploading' }));

        try {
            const uploadPromises = acceptedFiles.map(file => 
                DocumentService.uploadDocument({
                    fileType: 'additional',
                    file: file,
                }, {
                    additionalDocumentsLimit
                })
            );

            const responses = await Promise.all(uploadPromises);
            const successfulUploads = responses
                .filter(response => response.status === 'success' && response.document)
                .map(response => response.document!);

            if (successfulUploads.length > 0) {
                setUploadedDocs(prev => ({
                    ...prev,
                    additional: [...prev.additional, ...successfulUploads],
                }));
                setUploadStatus(prev => ({ ...prev, additional: 'success' }));
                setAlertMessage({ 
                    type: 'success', 
                    message: `${successfulUploads.length} additional document(s) uploaded successfully` 
                });
            } else {
                setUploadStatus(prev => ({ ...prev, additional: 'error' }));
                setAlertMessage({ type: 'error', message: 'Failed to upload additional documents' });
            }
        } catch (error) {
            setUploadStatus(prev => ({ ...prev, additional: 'error' }));
            setAlertMessage({ type: 'error', message: 'Failed to upload additional documents' });
        }
    }, [uploadedDocs.additional.length, additionalDocumentsLimit]);

    const handleDeleteAdditionalDoc = async (docId: string) => {
        try {
            await DocumentService.deleteDocument(docId);
            setUploadedDocs(prev => ({
                ...prev,
                additional: prev.additional.filter(doc => doc.id !== docId),
            }));
            setAlertMessage({ type: 'success', message: 'Document deleted successfully' });
        } catch (error) {
            setAlertMessage({ type: 'error', message: 'Failed to delete document' });
        }
    };

    const { getRootProps: getCvRootProps, getInputProps: getCvInputProps, isDragActive: isCvDragActive } = useDropzone({
        onDrop: onCvDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        },
        maxFiles: 1,
        disabled: uploadStatus.cv === 'uploading' || uploadedDocs.cv !== undefined,
    });

    const { getRootProps: getAdditionalRootProps, getInputProps: getAdditionalInputProps, isDragActive: isAdditionalDragActive } = useDropzone({
        onDrop: onAdditionalDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/png': ['.png'],
        },
        maxFiles: additionalDocumentsLimit - uploadedDocs.additional.length,
        disabled: uploadStatus.additional === 'uploading' || uploadedDocs.additional.length >= additionalDocumentsLimit,
    });

    const getStatusIcon = (status: 'idle' | 'uploading' | 'success' | 'error') => {
        switch (status) {
            case 'uploading':
                return <CircularProgress size={20} />;
            case 'success':
                return <CheckCircleIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            default:
                return null;
        }
    };

    const canAnalyzeProfile = uploadedDocs.linkedin || uploadedDocs.cv || uploadedDocs.additional.length > 0;

    // Check analysis limit on component mount
    useEffect(() => {
        const checkLimit = async () => {
            try {
                const auth = getAuth();
                const user = auth.currentUser;
                if (user) {
                    // Pro users get unlimited analyses
                    if (isPro || hasFeature('unlimited_profile_analyses')) {
                        setAnalysisLimit({ canAnalyze: true, remainingCount: -1, resetDate: '' }); // -1 indicates unlimited
                    } else {
                        const limit = await checkProfileAnalysisLimit(user.uid);
                        setAnalysisLimit(limit);
                    }
                }
            } catch (error) {
                console.error('Error checking analysis limit:', error);
            }
        };

        checkLimit();
    }, [isPro, hasFeature]);

    // Load past analyses
    const loadPastAnalyses = async () => {
        setIsLoadingPastAnalyses(true);
        try {
            const response = await getGTVProfileAnalyses(10);
            if (response.status === 'success') {
                setPastAnalyses(response.analyses);
            } else {
                setAlertMessage({
                    type: 'error',
                    message: 'Failed to load past analyses'
                });
            }
        } catch (error) {
            console.error('Error loading past analyses:', error);
            setAlertMessage({
                type: 'error',
                message: 'Failed to load past analyses'
            });
        } finally {
            setIsLoadingPastAnalyses(false);
        }
    };

    // Load past analyses when component mounts
    useEffect(() => {
        loadPastAnalyses();
    }, []);

    // Load past analyses when switching to Past Analyses tab
    useEffect(() => {
        if (currentTab === 1) {
            loadPastAnalyses();
        }
    }, [currentTab]);

    // Handle deleting a past analysis
    const handleDeleteAnalysis = async (analysisId: string) => {
        try {
            const response = await deleteGTVProfileAnalysis(analysisId);
            if (response.status === 'success') {
                setPastAnalyses(prev => prev.filter(analysis => analysis.id !== analysisId));
                setAlertMessage({
                    type: 'success',
                    message: 'Analysis deleted successfully'
                });
            } else {
                setAlertMessage({
                    type: 'error',
                    message: 'Failed to delete analysis'
                });
            }
        } catch (error) {
            console.error('Error deleting analysis:', error);
            setAlertMessage({
                type: 'error',
                message: 'Failed to delete analysis'
            });
        }
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Unknown date';
        }
    };

    // Analyze profile
    const handleAnalyzeProfile = async () => {
        if (!canAnalyzeProfile) {
            setAlertMessage({ 
                type: 'error', 
                message: 'Please upload at least one document (LinkedIn profile, CV, or additional documents) to analyze your profile' 
            });
            return;
        }

        // Check analysis limit
        if (analysisLimit && !analysisLimit.canAnalyze) {
            setAlertMessage({ 
                type: 'error', 
                message: `You've reached your daily limit of 3 profile analyses. You can analyze again tomorrow.` 
            });
            return;
        }

        setIsAnalyzing(true);
        setAlertMessage(null);
        setAnalysisResult(null);

        // Increment analytics count
        try {
            await incrementProfileScored();
        } catch (error) {
            console.error('Error incrementing analytics count:', error);
            // Continue with analysis even if analytics increment fails
        }

        // Track analysis attempt
        trackFeatureUsage('profile_analysis', 'global_talent_visa');
        trackCustomEvent('profile_analysis_initiated', {
            visa_type: 'global_talent_visa',
            category: selectedCategory,
            has_linkedin: !!uploadedDocs.linkedin,
            has_cv: !!uploadedDocs.cv,
            has_additional_docs: uploadedDocs.additional.length > 0,
            additional_docs_count: uploadedDocs.additional.length,
        });

        try {
            const response = await analyzeGTVProfile({
                category: selectedCategory
            });

            // Increment analysis count
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                await incrementProfileAnalysisCount(user.uid);
                // Update the limit state
                const newLimit = await checkProfileAnalysisLimit(user.uid);
                setAnalysisLimit(newLimit);
            }

            // Track successful analysis
            trackCustomEvent('profile_analysis_completed', {
                visa_type: 'global_talent_visa',
                category: selectedCategory,
                has_additional_docs: uploadedDocs.additional.length > 0,
            });

            setAnalysisResult(response);
            setAlertMessage({
                type: 'success',
                message: 'Profile analysis completed successfully!'
            });

            // Reload past analyses to include the new one
            loadPastAnalyses();
        } catch (error) {
            console.error('Error analyzing profile:', error);
            
            // Track analysis error
            trackError(error instanceof Error ? error : new Error('Analysis failed'), 'profile_analysis');

            setAlertMessage({
                type: 'error',
                message: error instanceof Error ? error.message : 'Failed to analyze profile'
            });
        } finally {
            setIsAnalyzing(false);
        }
    };

    return (
        <AuthLayout>
            <Box sx={{ 
                background: theme.background, 
                minHeight: 'calc(100vh - 64px)',
                pt: 2,
                pb: 2
            }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3 } }}>
                    {/* Header with Tabs */}
                    <Box sx={{ mb: 3, textAlign: 'center', position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <Chip 
                                label="FREE" 
                                size="small" 
                                sx={{ 
                                    background: '#4CAF50', 
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    height: 24,
                                    mr: 2
                                }} 
                            />
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontWeight: 700, 
                                    background: theme.gradientText,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}
                            >
                                GTV Eligibility Checker
                            </Typography>
                        </Box>
                        <Typography 
                            variant="body2" 
                            sx={{ color: theme.textSecondary, maxWidth: 600, mx: 'auto', mb: 3 }}
                        >
                            Get a comprehensive analysis of your profile for Global Talent Visa eligibility. Upload at least one document (LinkedIn, CV, or supporting documents) to get started.
                        </Typography>
                        
                        {/* Tabs */}
                        <Box sx={{ borderBottom: 1, borderColor: theme.borderColor, mb: 3 }}>
                            <Tabs 
                                value={currentTab} 
                                onChange={handleTabChange} 
                                centered
                                sx={{
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: theme.primary,
                                    },
                                    '& .MuiTab-root': {
                                        color: theme.textSecondary,
                                        fontWeight: 500,
                                        '&.Mui-selected': {
                                            color: theme.primary,
                                        },
                                    },
                                }}
                            >
                                <Tab 
                                    icon={<AnalyticsIcon />} 
                                    label="New Analysis" 
                                    iconPosition="start"
                                    sx={{ fontSize: '1rem', textTransform: 'none' }}
                                />
                                <Tab 
                                    icon={<HistoryIcon />} 
                                    label={`Past Analyses ${pastAnalyses.length > 0 ? `(${pastAnalyses.length})` : ''}`}
                                    iconPosition="start"
                                    sx={{ fontSize: '1rem', textTransform: 'none' }}
                                />
                            </Tabs>
                        </Box>
                        
                        {/* Analysis Limit Display */}
                        {analysisLimit && currentTab === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Chip
                                    label={analysisLimit.remainingCount === -1 ? 'Unlimited analyses' : `${analysisLimit.remainingCount}/3 analyses remaining today`}
                                    size="medium"
                                    color={analysisLimit.remainingCount === -1 ? "success" : analysisLimit.remainingCount <= 1 ? "error" : "warning"}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        px: 2,
                                        py: 1,
                                        '& .MuiChip-label': {
                                            px: 1,
                                        }
                                    }}
                                />
                            </Box>
                        )}
                        
                        {/* Manage Documents Button */}
                        {currentTab === 0 && (
                            <Box sx={{ 
                                position: { xs: 'static', sm: 'absolute' }, 
                                top: 0, 
                                right: 0,
                                mt: { xs: 2, sm: 0 },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<FolderIcon />}
                                    onClick={() => setDocumentManagerOpen(true)}
                                    sx={{
                                        borderRadius: 2,
                                        borderColor: theme.borderColor,
                                        color: theme.textPrimary,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        px: { xs: 2, sm: 3 },
                                        '&:hover': {
                                            borderColor: theme.primary,
                                            backgroundColor: 'rgba(254, 106, 46, 0.08)',
                                            color: theme.primary,
                                        }
                                    }}
                                >
                                    Manage Documents
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Alert Messages */}
                    {alertMessage && (
                        <Alert 
                            severity={alertMessage.type} 
                            sx={{ mb: 3 }}
                            onClose={() => setAlertMessage(null)}
                        >
                            {alertMessage.message}
                        </Alert>
                    )}

                    {/* Tab Content */}
                    {currentTab === 0 && (
                        <Box>
                            {/* New Analysis Tab Content */}
                            
                            {/* Category Selection Section */}
                            <Box sx={{ mb: 4 }}>
                                <Card sx={{ 
                                    background: theme.cardBackground, 
                                    border: `1px solid ${theme.borderColor}`,
                                    borderRadius: 3,
                                    boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                }}>
                                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                        <FormControl component="fieldset">
                                            <FormLabel 
                                                component="legend" 
                                                sx={{ 
                                                    color: theme.textPrimary,
                                                    fontWeight: 600,
                                                    fontSize: '1.1rem',
                                                    mb: 2,
                                                    '&.Mui-focused': {
                                                        color: theme.textPrimary,
                                                    }
                                                }}
                                            >
                                                Select Your GTV Category
                                            </FormLabel>
                                            <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 3, opacity: 0.8 }}>
                                                Choose the category that best matches your professional background for the most accurate analysis.
                                            </Typography>
                                            <RadioGroup
                                                value={selectedCategory}
                                                onChange={handleCategoryChange}
                                                sx={{ 
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', md: 'row' },
                                                    gap: { xs: 1, md: 3 },
                                                }}
                                            >
                                                <FormControlLabel 
                                                    value="digital_technology" 
                                                    control={
                                                        <Radio 
                                                            sx={{ 
                                                                color: theme.textSecondary,
                                                                '&.Mui-checked': {
                                                                    color: theme.primary,
                                                                },
                                                            }} 
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <ComputerIcon sx={{ mr: 1, color: theme.primary }} />
                                                            <Box>
                                                                <Typography sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                                                                    Digital Technology
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: theme.textSecondary, display: 'block' }}>
                                                                    Tech professionals, developers, CTOs
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ 
                                                        mr: { xs: 0, md: 2 },
                                                        mb: { xs: 1, md: 0 },
                                                        border: `1px solid ${selectedCategory === 'digital_technology' ? theme.primary : theme.borderColor}`,
                                                        borderRadius: 2,
                                                        p: 1.5,
                                                        m: 0,
                                                        background: selectedCategory === 'digital_technology' 
                                                            ? `${theme.primary}10`
                                                            : 'transparent',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: theme.primary,
                                                            background: `${theme.primary}08`,
                                                        }
                                                    }}
                                                />
                                                <FormControlLabel 
                                                    value="academia_research" 
                                                    control={
                                                        <Radio 
                                                            sx={{ 
                                                                color: theme.textSecondary,
                                                                '&.Mui-checked': {
                                                                    color: theme.primary,
                                                                },
                                                            }} 
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <SchoolIcon sx={{ mr: 1, color: theme.primary }} />
                                                            <Box>
                                                                <Typography sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                                                                    Academia or Research
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: theme.textSecondary, display: 'block' }}>
                                                                    Researchers, professors, scientists
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ 
                                                        mr: { xs: 0, md: 2 },
                                                        mb: { xs: 1, md: 0 },
                                                        border: `1px solid ${selectedCategory === 'academia_research' ? theme.primary : theme.borderColor}`,
                                                        borderRadius: 2,
                                                        p: 1.5,
                                                        m: 0,
                                                        background: selectedCategory === 'academia_research' 
                                                            ? `${theme.primary}10`
                                                            : 'transparent',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: theme.primary,
                                                            background: `${theme.primary}08`,
                                                        }
                                                    }}
                                                />
                                                <FormControlLabel 
                                                    value="arts_culture" 
                                                    control={
                                                        <Radio 
                                                            sx={{ 
                                                                color: theme.textSecondary,
                                                                '&.Mui-checked': {
                                                                    color: theme.primary,
                                                                },
                                                            }} 
                                                        />
                                                    }
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <PaletteIcon sx={{ mr: 1, color: theme.primary }} />
                                                            <Box>
                                                                <Typography sx={{ color: theme.textPrimary, fontWeight: 500 }}>
                                                                    Arts and Culture
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: theme.textSecondary, display: 'block' }}>
                                                                    Artists, designers, cultural practitioners
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    }
                                                    sx={{ 
                                                        border: `1px solid ${selectedCategory === 'arts_culture' ? theme.primary : theme.borderColor}`,
                                                        borderRadius: 2,
                                                        p: 1.5,
                                                        m: 0,
                                                        background: selectedCategory === 'arts_culture' 
                                                            ? `${theme.primary}10`
                                                            : 'transparent',
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            borderColor: theme.primary,
                                                            background: `${theme.primary}08`,
                                                        }
                                                    }}
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </CardContent>
                                </Card>
                            </Box>
                            
                            {/* Loading State */}
                            {isLoadingDocuments && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    py: 4,
                                    mb: 3
                                }}>
                                    <CircularProgress sx={{ mr: 2 }} />
                                    <Typography variant="body1" sx={{ color: theme.textSecondary }}>
                                        Loading your documents...
                                    </Typography>
                                </Box>
                            )}

                            {/* Current Analysis Result */}
                            {analysisResult && (
                                <Box sx={{ mb: 3 }}>
                                    <Card sx={{ 
                                        background: theme.cardBackground, 
                                        border: `1px solid ${theme.borderColor}`,
                                        borderRadius: 3,
                                        boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                    }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    background: 'rgba(76, 175, 80, 0.1)',
                                                    mr: 2
                                                }}>
                                                    <AssessmentIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                                                </Box>
                                                <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                                                    Profile Analysis Results
                                                </Typography>
                                                <Chip 
                                                    label="FREE" 
                                                    size="small" 
                                                    sx={{ 
                                                        background: '#4CAF50', 
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        ml: 2
                                                    }} 
                                                />
                                            </Box>
                                            
                                            <Paper sx={{ 
                                                p: 3, 
                                                background: isDarkMode ? '#1a1a1a' : '#f8f9fa',
                                                border: `1px solid ${theme.borderColor}`,
                                                borderRadius: 2,
                                                maxHeight: 400,
                                                overflow: 'auto'
                                            }}>
                                                <MarkdownRenderer 
                                                    content={analysisResult.analysis}
                                                    sx={{
                                                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                            color: theme.textPrimary,
                                                            fontWeight: 600,
                                                            mb: 1.5,
                                                            mt: 2,
                                                        },
                                                        '& h1': { fontSize: '1.5rem' },
                                                        '& h2': { fontSize: '1.3rem' },
                                                        '& h3': { fontSize: '1.1rem' },
                                                        '& p': {
                                                            color: theme.textPrimary,
                                                            lineHeight: 1.6,
                                                            mb: 1.5,
                                                        },
                                                        '& ul, & ol': {
                                                            color: theme.textPrimary,
                                                            pl: 2,
                                                            mb: 1.5,
                                                        },
                                                        '& li': {
                                                            mb: 0.5,
                                                            lineHeight: 1.5,
                                                        },
                                                        '& strong, & b': {
                                                            fontWeight: 600,
                                                            color: theme.textPrimary,
                                                        },
                                                        '& em, & i': {
                                                            fontStyle: 'italic',
                                                        },
                                                        '& code': {
                                                            background: isDarkMode ? '#2d2d2d' : '#f0f0f0',
                                                            color: isDarkMode ? '#e6e6e6' : '#333',
                                                            padding: '2px 6px',
                                                            borderRadius: 1,
                                                            fontSize: '0.9em',
                                                            fontFamily: 'monospace',
                                                        },
                                                        '& pre': {
                                                            background: isDarkMode ? '#2d2d2d' : '#f0f0f0',
                                                            color: isDarkMode ? '#e6e6e6' : '#333',
                                                            padding: 2,
                                                            borderRadius: 1,
                                                            overflow: 'auto',
                                                            mb: 1.5,
                                                            '& code': {
                                                                background: 'transparent',
                                                                padding: 0,
                                                            }
                                                        },
                                                        '& blockquote': {
                                                            borderLeft: `4px solid ${theme.primary}`,
                                                            pl: 2,
                                                            ml: 0,
                                                            mb: 1.5,
                                                            fontStyle: 'italic',
                                                            color: theme.textSecondary,
                                                        },
                                                        '& table': {
                                                            width: '100%',
                                                            borderCollapse: 'collapse',
                                                            mb: 1.5,
                                                            '& th, & td': {
                                                                border: `1px solid ${theme.borderColor}`,
                                                                padding: 1,
                                                                textAlign: 'left',
                                                            },
                                                            '& th': {
                                                                background: isDarkMode ? '#2d2d2d' : '#f5f5f5',
                                                                fontWeight: 600,
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Paper>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Past Analyses Tab Content */}
                    {currentTab === 1 && (
                        <Box>
                            {isLoadingPastAnalyses ? (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    py: 8
                                }}>
                                    <CircularProgress sx={{ mr: 2 }} />
                                    <Typography variant="body1" sx={{ color: theme.textSecondary }}>
                                        Loading past analyses...
                                    </Typography>
                                </Box>
                            ) : pastAnalyses.length === 0 ? (
                                <Box sx={{ 
                                    textAlign: 'center', 
                                    py: 8,
                                    px: 2
                                }}>
                                    <HistoryIcon sx={{ 
                                        fontSize: 64, 
                                        color: theme.textSecondary, 
                                        mb: 2,
                                        opacity: 0.5
                                    }} />
                                    <Typography variant="h6" sx={{ color: theme.textPrimary, mb: 1 }}>
                                        No Past Analyses Yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 3 }}>
                                        Complete your first profile analysis to see your history here.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setCurrentTab(0)}
                                        startIcon={<AnalyticsIcon />}
                                        sx={{
                                            borderColor: theme.primary,
                                            color: theme.primary,
                                            '&:hover': {
                                                borderColor: theme.primary,
                                                backgroundColor: 'rgba(254, 106, 46, 0.08)',
                                            }
                                        }}
                                    >
                                        Start New Analysis
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
                                    {pastAnalyses.map((analysis, index) => (
                                        <Accordion 
                                            key={analysis.id}
                                            sx={{ 
                                                mb: 2,
                                                background: theme.cardBackground,
                                                border: `1px solid ${theme.borderColor}`,
                                                borderRadius: '12px !important',
                                                boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                                '&:before': { display: 'none' },
                                                '&.Mui-expanded': {
                                                    margin: '0 0 16px 0',
                                                }
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon sx={{ color: theme.textPrimary }} />}
                                                sx={{
                                                    '& .MuiAccordionSummary-content': {
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    },
                                                    py: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: 2,
                                                        background: 'rgba(156, 39, 176, 0.1)',
                                                        mr: 2
                                                    }}>
                                                        <AssessmentIcon sx={{ color: '#9C27B0', fontSize: 20 }} />
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="body1" sx={{ color: theme.textPrimary, fontWeight: 600 }}>
                                                            Analysis #{pastAnalyses.length - index}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                            <CalendarTodayIcon sx={{ color: theme.textSecondary, fontSize: 14, mr: 0.5 }} />
                                                            <Typography variant="caption" sx={{ color: theme.textSecondary }}>
                                                                {formatDate(analysis.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Tooltip title="Delete analysis">
                                                    <IconButton 
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteAnalysis(analysis.id);
                                                        }}
                                                        sx={{ 
                                                            ml: 1,
                                                            color: theme.textSecondary,
                                                            '&:hover': {
                                                                color: '#f44336',
                                                                backgroundColor: 'rgba(244, 67, 54, 0.08)',
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                                                <Paper sx={{ 
                                                    p: 3, 
                                                    background: isDarkMode ? '#0d0d0d' : '#ffffff',
                                                    border: `1px solid ${theme.borderColor}`,
                                                    borderRadius: 2,
                                                    maxHeight: 400,
                                                    overflow: 'auto'
                                                }}>
                                                    <MarkdownRenderer 
                                                        content={analysis.analysis}
                                                        sx={{
                                                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                                                color: theme.textPrimary,
                                                                fontWeight: 600,
                                                                mb: 1,
                                                                mt: 1,
                                                                fontSize: '0.95rem',
                                                            },
                                                            '& p': {
                                                                color: theme.textPrimary,
                                                                lineHeight: 1.5,
                                                                mb: 1,
                                                                fontSize: '0.9rem',
                                                            },
                                                            '& ul, & ol': {
                                                                color: theme.textPrimary,
                                                                pl: 1.5,
                                                                mb: 1,
                                                                fontSize: '0.9rem',
                                                            },
                                                            '& li': {
                                                                mb: 0.25,
                                                                lineHeight: 1.4,
                                                            },
                                                            '& strong, & b': {
                                                                fontWeight: 600,
                                                                color: theme.textPrimary,
                                                            },
                                                        }}
                                                    />
                                                </Paper>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Document Upload Sections - Only show in New Analysis tab */}
                    {currentTab === 0 && (
                        <>
                            {/* LinkedIn Profile Section */}
                            <Box sx={{ mb: 3 }}>
                                <Card sx={{ 
                                    background: theme.cardBackground, 
                                    border: `1px solid ${theme.borderColor}`,
                                    borderRadius: 3,
                                    boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        boxShadow: `0 4px 16px ${theme.shadowColor}`,
                                    }
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2,
                                                background: 'rgba(0, 119, 181, 0.1)',
                                                mr: 2
                                            }}>
                                                <LinkedInIcon sx={{ color: '#0077B5', fontSize: 24 }} />
                                            </Box>
                                            <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 600, fontSize: '1.2rem' }}>
                                                LinkedIn Profile
                                            </Typography>
                                            <Chip 
                                                label="Optional" 
                                                size="small" 
                                                color="default" 
                                                sx={{ ml: 2, height: 24, fontWeight: 500 }} 
                                            />
                                            <Box sx={{ ml: 2 }}>
                                                {getStatusIcon(uploadStatus.linkedin)}
                                            </Box>
                                        </Box>
                                        
                                        {!uploadedDocs.linkedin ? (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                gap: { xs: 2, sm: 2 }, 
                                                alignItems: { xs: 'stretch', sm: 'flex-end' }
                                            }}>
                                                <TextField
                                                    fullWidth
                                                    label="LinkedIn Profile URL"
                                                    placeholder="https://www.linkedin.com/in/your-profile"
                                                    value={linkedinUrl}
                                                    onChange={(e) => setLinkedinUrl(e.target.value)}
                                                    disabled={uploadStatus.linkedin === 'uploading'}
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            background: theme.inputBackground,
                                                            borderRadius: 2,
                                                            color: theme.textPrimary,
                                                            '& input': {
                                                                color: theme.textPrimary,
                                                            },
                                                            '& input::placeholder': {
                                                                color: theme.textSecondary,
                                                                opacity: 0.7,
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.primary,
                                                            },
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: theme.borderColor,
                                                            }
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: theme.textSecondary,
                                                            '&.Mui-focused': {
                                                                color: theme.primary,
                                                            }
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={handleLinkedInSubmit}
                                                    disabled={uploadStatus.linkedin === 'uploading' || !linkedinUrl.trim()}
                                                    startIcon={uploadStatus.linkedin === 'uploading' ? <CircularProgress size={16} /> : <LinkedInIcon />}
                                                    sx={{ 
                                                        whiteSpace: 'nowrap', 
                                                        minHeight: 56,
                                                        px: { xs: 2, sm: 3 },
                                                        borderRadius: 2,
                                                        background: `linear-gradient(135deg, ${theme.primary} 0%, #FF8F5A 100%)`,
                                                        '&:hover': {
                                                            background: `linear-gradient(135deg, #FF8F5A 0%, #E5511A 100%)`,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px rgba(254, 106, 46, 0.3)`,
                                                        }
                                                    }}
                                                >
                                                    {uploadStatus.linkedin === 'uploading' ? 'Saving...' : 'Save Profile'}
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { xs: 'stretch', sm: 'center' }, 
                                                justifyContent: 'space-between',
                                                p: 2,
                                                borderRadius: 2,
                                                background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)`,
                                                border: `1px solid rgba(76, 175, 80, 0.2)`,
                                                gap: { xs: 1, sm: 0 }
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CheckCircleIcon sx={{ color: '#4CAF50', mr: 1 }} />
                                                    <Box>
                                                        <Typography sx={{ color: theme.textPrimary, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                            LinkedIn Profile Successfully Saved
                                                        </Typography>
                                                        {uploadedDocs.linkedin?.linkUrl && (
                                                            <Typography variant="caption" sx={{ color: theme.textSecondary, display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
                                                                {uploadedDocs.linkedin.linkUrl}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={async () => {
                                                            if (uploadedDocs.linkedin) {
                                                                try {
                                                                    await DocumentService.deleteDocument(uploadedDocs.linkedin.id);
                                                                    setUploadedDocs(prev => ({ ...prev, linkedin: undefined }));
                                                                    setUploadStatus(prev => ({ ...prev, linkedin: 'idle' }));
                                                                    setLinkedinUrl('');
                                                                    setAlertMessage({ type: 'success', message: 'LinkedIn profile removed. You can now add a new one.' });
                                                                } catch (error) {
                                                                    setAlertMessage({ type: 'error', message: 'Failed to remove LinkedIn profile' });
                                                                }
                                                            }
                                                        }}
                                                        sx={{
                                                            borderColor: theme.borderColor,
                                                            color: theme.textSecondary,
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                            px: { xs: 1, sm: 1.5 },
                                                            py: { xs: 0.25, sm: 0.5 },
                                                            '&:hover': {
                                                                borderColor: theme.primary,
                                                                color: theme.primary,
                                                            }
                                                        }}
                                                    >
                                                        Replace
                                                    </Button>
                                                    <Chip 
                                                        label="✓ Complete" 
                                                        size="small" 
                                                        sx={{ 
                                                            background: '#4CAF50', 
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                                        }} 
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* CV and Additional Documents - Two Column Layout */}
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', lg: 'row' },
                                gap: { xs: 2, lg: 3 }, 
                                mb: 3, 
                                alignItems: 'stretch' 
                            }}>
                                {/* CV Upload Section */}
                                <Box sx={{ flex: 1 }}>
                                    <Card sx={{ 
                                        background: theme.cardBackground, 
                                        border: `1px solid ${theme.borderColor}`, 
                                        height: '100%',
                                        borderRadius: 3,
                                        boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${theme.shadowColor}`,
                                        }
                                    }}>
                                        <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    background: `rgba(254, 106, 46, 0.1)`,
                                                    mr: 2
                                                }}>
                                                    <DescriptionIcon sx={{ color: theme.primary, fontSize: 24 }} />
                                                </Box>
                                                <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 600, fontSize: '1.2rem' }}>
                                                    CV/Resume
                                                </Typography>
                                                <Chip 
                                                    label="Recommended" 
                                                    size="small" 
                                                    color="warning" 
                                                    sx={{ ml: 2, height: 24, fontWeight: 500 }} 
                                                />
                                                <Box sx={{ ml: 2 }}>
                                                    {getStatusIcon(uploadStatus.cv)}
                                                </Box>
                                            </Box>

                                            <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 2, opacity: 0.8 }}>
                                                Upload your curriculum vitae or resume document for comprehensive profile analysis.
                                            </Typography>

                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                {!uploadedDocs.cv ? (
                                                    <Box
                                                        {...getCvRootProps()}
                                                        sx={{
                                                            border: `2px dashed ${isCvDragActive ? theme.primary : theme.borderColor}`,
                                                            borderRadius: 3,
                                                            p: 4,
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            background: isCvDragActive 
                                                                ? `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)` 
                                                                : `linear-gradient(135deg, ${theme.cardBackground} 0%, rgba(255,255,255,0.02) 100%)`,
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            '&:hover': {
                                                                borderColor: theme.primary,
                                                                background: `linear-gradient(135deg, ${theme.primary}10 0%, ${theme.primary}05 100%)`,
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: `radial-gradient(circle at center, ${theme.primary}05 0%, transparent 70%)`,
                                                                opacity: 0,
                                                                transition: 'opacity 0.3s ease',
                                                            },
                                                            '&:hover::before': {
                                                                opacity: 1,
                                                            }
                                                        }}
                                                    >
                                                        <input {...getCvInputProps()} />
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            width: 64,
                                                            height: 64,
                                                            borderRadius: '50%',
                                                            background: `linear-gradient(135deg, ${theme.primary}20 0%, ${theme.primary}10 100%)`,
                                                            mx: 'auto',
                                                            mb: 2
                                                        }}>
                                                            <CloudUploadIcon sx={{ fontSize: 32, color: theme.primary }} />
                                                        </Box>
                                                        <Typography variant="h6" sx={{ color: theme.textPrimary, mb: 1, fontWeight: 600 }}>
                                                            {isCvDragActive ? 'Drop your CV here' : 'Upload your CV'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 1 }}>
                                                            Drag and drop or click to browse
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: theme.textSecondary, opacity: 0.7 }}>
                                                            PDF, DOC, DOCX (Max 10MB)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ 
                                                        border: `1px solid rgba(76, 175, 80, 0.2)`,
                                                        borderRadius: 3,
                                                        p: { xs: 2, sm: 3 },
                                                        display: 'flex', 
                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                        alignItems: { xs: 'stretch', sm: 'center' }, 
                                                        justifyContent: 'space-between',
                                                        background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)`,
                                                        minHeight: 80,
                                                        gap: { xs: 1, sm: 0 }
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                width: { xs: 32, sm: 40 },
                                                                height: { xs: 32, sm: 40 },
                                                                borderRadius: 2,
                                                                background: 'rgba(76, 175, 80, 0.1)',
                                                                mr: 2
                                                            }}>
                                                                <DescriptionIcon sx={{ color: '#4CAF50', fontSize: { xs: 16, sm: 20 } }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{ color: theme.textPrimary, fontWeight: 500, mb: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                                    {uploadedDocs.cv.fileName}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: theme.textSecondary }}>
                                                                    CV successfully uploaded
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Chip 
                                                            label="✓ Complete" 
                                                            size="small" 
                                                            sx={{ 
                                                                background: '#4CAF50', 
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                                            }} 
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>

                                {/* Additional Documents Section */}
                                <Box sx={{ flex: 1 }}>
                                    <Card sx={{ 
                                        background: theme.cardBackground, 
                                        border: `1px solid ${theme.borderColor}`, 
                                        height: '100%',
                                        borderRadius: 3,
                                        boxShadow: `0 2px 8px ${theme.shadowColor}`,
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: `0 4px 16px ${theme.shadowColor}`,
                                        }
                                    }}>
                                        <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 2,
                                                    background: `rgba(254, 106, 46, 0.1)`,
                                                    mr: 2
                                                }}>
                                                    <AttachFileIcon sx={{ color: theme.primary, fontSize: 24 }} />
                                                </Box>
                                                <Typography variant="h6" sx={{ color: theme.textPrimary, fontWeight: 600, fontSize: '1.2rem' }}>
                                                    Additional Documents
                                                </Typography>
                                                <Chip 
                                                    label={`${uploadedDocs.additional.length}/${additionalDocumentsLimit}`} 
                                                    size="small" 
                                                    color={uploadedDocs.additional.length === 0 ? "default" : "primary"}
                                                    sx={{ ml: 2, height: 24, fontWeight: 500 }} 
                                                />
                                                <Box sx={{ ml: 2 }}>
                                                    {getStatusIcon(uploadStatus.additional)}
                                                </Box>
                                            </Box>

                                            <Typography variant="body2" sx={{ color: theme.textSecondary, mb: 2, opacity: 0.8 }}>
                                                Upload supporting documents like certificates, awards, reference letters, etc.
                                            </Typography>

                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                {/* Uploaded Documents List */}
                                                {uploadedDocs.additional.length > 0 && (
                                                    <Box sx={{ mb: 1.5 }}>
                                                        <List dense sx={{ py: 0 }}>
                                                            {uploadedDocs.additional.map((doc) => (
                                                                <ListItem key={doc.id} sx={{ px: 0, py: 0.5 }}>
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <AttachFileIcon sx={{ color: theme.textSecondary, fontSize: 16 }} />
                                                                    </ListItemIcon>
                                                                    <ListItemText 
                                                                        primary={doc.fileName}
                                                                        primaryTypographyProps={{
                                                                            sx: { fontSize: '0.8rem', color: theme.textPrimary }
                                                                        }}
                                                                    />
                                                                    <ListItemSecondaryAction>
                                                                        <Tooltip title="Delete document">
                                                                            <IconButton 
                                                                                edge="end" 
                                                                                size="small"
                                                                                onClick={() => handleDeleteAdditionalDoc(doc.id)}
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </ListItemSecondaryAction>
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>
                                                )}

                                                {/* Upload Area */}
                                                {uploadedDocs.additional.length < additionalDocumentsLimit && (
                                                    <Box
                                                        {...getAdditionalRootProps()}
                                                        sx={{
                                                            border: `2px dashed ${isAdditionalDragActive ? theme.primary : theme.borderColor}`,
                                                            borderRadius: 3,
                                                            p: uploadedDocs.additional.length > 0 ? 2 : 4,
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            background: isAdditionalDragActive 
                                                                ? `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}08 100%)` 
                                                                : `linear-gradient(135deg, ${theme.cardBackground} 0%, rgba(255,255,255,0.02) 100%)`,
                                                            flex: 1,
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            '&:hover': {
                                                                borderColor: theme.primary,
                                                                background: `linear-gradient(135deg, ${theme.primary}10 0%, ${theme.primary}05 100%)`,
                                                                transform: 'translateY(-2px)',
                                                            },
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                background: `radial-gradient(circle at center, ${theme.primary}05 0%, transparent 70%)`,
                                                                opacity: 0,
                                                                transition: 'opacity 0.3s ease',
                                                            },
                                                            '&:hover::before': {
                                                                opacity: 1,
                                                            }
                                                        }}
                                                    >
                                                        <input {...getAdditionalInputProps()} />
                                                        {uploadedDocs.additional.length === 0 && (
                                                            <Box sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                width: 64,
                                                                height: 64,
                                                                borderRadius: '50%',
                                                                background: `linear-gradient(135deg, ${theme.primary}20 0%, ${theme.primary}10 100%)`,
                                                                mx: 'auto',
                                                                mb: 2
                                                            }}>
                                                                <CloudUploadIcon sx={{ fontSize: 32, color: theme.primary }} />
                                                            </Box>
                                                        )}
                                                        {uploadedDocs.additional.length > 0 && (
                                                            <AttachFileIcon sx={{ fontSize: 28, color: theme.primary, mb: 1 }} />
                                                        )}
                                                        <Typography variant={uploadedDocs.additional.length > 0 ? "body2" : "h6"} sx={{ color: theme.textPrimary, mb: 0.5, fontWeight: 600 }}>
                                                            {isAdditionalDragActive ? 'Drop files here' : uploadedDocs.additional.length > 0 ? 'Add more documents' : 'Upload documents'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: theme.textSecondary, opacity: 0.7 }}>
                                                                                                                              PDF, DOC, DOCX, JPG, PNG<br/>(Max 10MB each, {additionalDocumentsLimit - uploadedDocs.additional.length} remaining)
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Analyze Button Section - Only show in New Analysis tab */}
                    {currentTab === 0 && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Button
                                variant="contained"
                                size="large"
                                disabled={!canAnalyzeProfile || isAnalyzing}
                                onClick={handleAnalyzeProfile}
                                startIcon={isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <AssessmentIcon />}
                                sx={{
                                    px: { xs: 4, sm: 6 },
                                    py: { xs: 1.5, sm: 2 },
                                    fontSize: { xs: '1rem', sm: '1.2rem' },
                                    fontWeight: 600,
                                    borderRadius: 3,
                                    width: { xs: '100%', sm: 'auto' },
                                    background: (canAnalyzeProfile && !isAnalyzing)
                                        ? `linear-gradient(135deg, #4CAF50 0%, #45A049 100%)`
                                        : theme.textSecondary,
                                    color: 'white',
                                    textTransform: 'none',
                                    boxShadow: (canAnalyzeProfile && !isAnalyzing)
                                        ? `0 4px 16px rgba(76, 175, 80, 0.3)`
                                        : 'none',
                                    transition: 'all 0.3s ease',
                                    '&:hover': (canAnalyzeProfile && !isAnalyzing) ? {
                                        background: `linear-gradient(135deg, #45A049 0%, #3D8B40 100%)`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 6px 20px rgba(76, 175, 80, 0.4)`,
                                    } : {},
                                    '&:disabled': {
                                        background: theme.textSecondary,
                                        color: 'white',
                                        cursor: 'not-allowed',
                                    }
                                }}
                            >
                                {isAnalyzing ? 'Analyzing...' : 'Analyze Profile'}
                            </Button>
                            
                            {!canAnalyzeProfile && !isAnalyzing && (
                                <Box sx={{ 
                                    mt: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2,
                                    background: `linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 193, 7, 0.05) 100%)`,
                                    border: `1px solid rgba(255, 193, 7, 0.2)`,
                                    textAlign: 'center'
                                }}>
                                    <InfoIcon sx={{ color: '#FFC107', mr: 1, fontSize: { xs: 18, sm: 20 } }} />
                                    <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        Please upload at least one document to analyze your profile
                                    </Typography>
                                </Box>
                            )}

                            {isAnalyzing && (
                                <Box sx={{ 
                                    mt: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2,
                                    background: `linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)`,
                                    border: `1px solid rgba(76, 175, 80, 0.2)`,
                                    textAlign: 'center'
                                }}>
                                    <CircularProgress size={16} sx={{ mr: 1, color: '#4CAF50' }} />
                                    <Typography variant="body2" sx={{ color: theme.textPrimary, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                        Analyzing your profile for GTV eligibility...
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Document Manager Dialog */}
            <DocumentManagerDialog 
                open={documentManagerOpen}
                onClose={() => setDocumentManagerOpen(false)}
            />
        </AuthLayout>
    );
};

export default ProfileScoreAnalyzer; 